import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { storyboardApi, STATUS_META } from './storyboardApi';
import BriefForm from './BriefForm';
import ConceptPanel from './ConceptPanel';
import ScenesGrid from './ScenesGrid';

const STEPS = [
  { id: 'brief', label: '1 · Brief' },
  { id: 'concept', label: '2 · Creative Concept' },
  { id: 'scenes', label: '3 · Scenes' },
];

export default function StoryboardEditor({ storyboardId, authHeader, onBack }) {
  const [board, setBoard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('brief');
  const pollRef = useRef(null);

  const loadDetail = useCallback(async () => {
    const { ok, data } = await storyboardApi.get(storyboardId, authHeader);
    if (ok) {
      setBoard(data.storyboard);
      setMessages(data.messages || []);
      setScenes(data.scenes || []);
      setLogoUrl(data.logo_url || null);
      // Land on the most relevant step
      const st = data.storyboard.status;
      if (st === 'generating' || st === 'complete' || (data.scenes || []).length > 0) setStep('scenes');
      else if (data.storyboard.concept) setStep('concept');
      else setStep('brief');
    }
    setLoading(false);
  }, [storyboardId, authHeader]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Poll scenes while generating (board status or any scene in flight)
  const generating = board?.status === 'generating'
    || scenes.some(s => s.status === 'generating' || s.status === 'pending');

  useEffect(() => {
    if (!generating) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const { ok, data } = await storyboardApi.pollScenes(storyboardId, authHeader);
      if (ok) {
        setScenes(data.scenes || []);
        setBoard(prev => (prev ? { ...prev, status: data.status, error_message: data.error_message } : prev));
      }
    }, 6000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [generating, storyboardId, authHeader]);

  if (loading || !board) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-gray-400" data-testid="sb-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading storyboard...
      </div>
    );
  }

  const meta = STATUS_META[board.status] || STATUS_META.briefing;

  return (
    <div className="p-4 md:p-6 max-w-5xl space-y-5" data-testid="sb-editor">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="sb-back-btn"
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Storyboards
        </button>
        <span className="text-gray-200">/</span>
        <span className="text-sm font-medium text-gray-900 truncate max-w-[240px]">
          {board.title || 'Untitled storyboard'}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.cls}`} data-testid="sb-status-badge">
          {meta.label}
        </span>
      </div>

      {/* Step nav */}
      <div className="flex items-center gap-2">
        {STEPS.map(s => {
          const disabled = (s.id === 'concept' && !board.concept)
            || (s.id === 'scenes' && !board.concept);
          return (
            <button
              key={s.id}
              data-testid={`sb-step-${s.id}`}
              onClick={() => !disabled && setStep(s.id)}
              disabled={disabled}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                step === s.id
                  ? 'bg-gray-900 text-white'
                  : disabled
                    ? 'bg-white/30 border border-gray-200/40 text-gray-300 cursor-not-allowed'
                    : 'bg-white/40 border border-gray-200/40 text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {step === 'brief' && (
        <BriefForm
          board={board}
          logoUrl={logoUrl}
          authHeader={authHeader}
          onBoardChange={(b) => setBoard(b)}
          onLogoUrlChange={setLogoUrl}
          onConceptGenerated={(concept, status) => {
            setBoard(prev => ({ ...prev, concept, status, title: prev.title || concept?.title }));
            setStep('concept');
          }}
        />
      )}

      {step === 'concept' && (
        <ConceptPanel
          board={board}
          messages={messages}
          authHeader={authHeader}
          onMessagesChange={setMessages}
          onBoardChange={setBoard}
          onGoToScenes={() => setStep('scenes')}
        />
      )}

      {step === 'scenes' && (
        <ScenesGrid
          board={board}
          scenes={scenes}
          authHeader={authHeader}
          onScenesChange={setScenes}
          onBoardChange={setBoard}
        />
      )}
    </div>
  );
}
